import axios from 'axios';
import { Vue } from 'vue-property-decorator';
import { Module, VuexModule, Action, Mutation } from 'vuex-module-decorators';
import { VisibilityRule, VisibilityRuleSet } from '@/models/VisibilityRule';

function getUrl(data: VisibilityRule) {
    if (data.formElement) {
        return '/api/visibility_rule_form_elements';
    } else {
        return '/api/visibility_rule_form_element_tags';
    }
}

export interface IVisibilityRuleSets {
    [element: string]: VisibilityRuleSet[];
}

@Module({ namespaced: true })
class VisibilityRuleSetsModule extends VuexModule {
    _all: IVisibilityRuleSets = {};

    get all(): IVisibilityRuleSets {
        return this._all;
    }

    @Mutation
    addRuleSet(set: VisibilityRuleSet) {
        if (!this._all[set.parent]) {
            Vue.set(this._all, set.parent, []);
        }
        const index = this._all[set.parent].findIndex((s: VisibilityRuleSet) => s.id === set.id);
        if (index === -1) {
            this._all[set.parent].push(set);
        }
    }

    @Mutation
    setRuleSet(set: VisibilityRuleSet) {
        const index = this._all[set.parent].findIndex((s: VisibilityRuleSet) => s.id === set.id);
        Vue.set(this._all[set.parent], index, set);
    }

    @Mutation
    setRuleSets({ parent, sets }: { parent: string; sets: VisibilityRuleSet[] }) {
        Vue.set(this._all, parent, sets);
    }

    @Mutation
    removeRuleSet(set: VisibilityRuleSet) {
        const index = this._all[set.parent].findIndex((s: VisibilityRuleSet) => s.id === set.id);
        this._all[set.parent].splice(index, 1);
    }

    @Mutation
    addRule({ set, rule }: { set: VisibilityRuleSet; rule: VisibilityRule }) {
        const setIndex = this._all[set.parent].findIndex((s: VisibilityRuleSet) => s.id === set.id);
        this._all[set.parent][setIndex].rules.push(rule);
    }

    @Mutation
    setRule({ set, rule }: { set: VisibilityRuleSet; rule: VisibilityRule }) {
        const setIndex = this._all[set.parent].findIndex((s: VisibilityRuleSet) => s.id === set.id);
        const index = this._all[set.parent][setIndex].rules.findIndex((r: VisibilityRule) => r.id === rule.id);
        this._all[set.parent][setIndex].rules.splice(index, 1, rule);
    }

    @Mutation
    removeRule({ set, rule }: { set: VisibilityRuleSet; rule: VisibilityRule }) {
        const setIndex = this._all[set.parent].findIndex((s: VisibilityRuleSet) => s.id === set.id);
        const index = this._all[set.parent][setIndex].rules.findIndex((r: VisibilityRule) => r.id === rule.id);
        this._all[set.parent][setIndex].rules.splice(index, 1);
    }

    @Action
    async read(url: string) {
        try {
            const r = await axios({
                method: 'GET',
                url,
            });
            this.context.commit('addRuleSet', new VisibilityRuleSet(r.data));
        } catch (e) {
            return e;
        }
    }

    @Action
    async create(data: VisibilityRuleSet) {
        try {
            const r: any = await axios({
                method: 'POST',
                url: '/api/visibility_rule_sets',
                data,
            });
            if (r.status !== 201) {
                throw Error('VisibilityRuleSet CREATE failed.');
            }
            this.context.commit('addRuleSet', new VisibilityRuleSet(r.data));
        } catch (e) {
            return e;
        }
    }

    @Action
    async update({ set, data }: { set: VisibilityRuleSet; data: VisibilityRule }) {
        try {
            const update = await axios({
                method: 'PUT',
                responseType: 'json',
                headers: {
                    'accept': 'application/ld+json',
                    'content-type': 'application/ld+json',
                },
                url: set.uri,
                data,
            });

            if (update.status !== 200) {
                throw Error('VisibilityRuleSet UPDATE failed.');
            }

            const r = await axios({
                method: 'GET',
                url: set.uri,
            });

            if (r.status !== 200) {
                throw Error('VisibilityRuleSet READ failed.');
            }
            this.context.commit('setRuleSet', new VisibilityRuleSet(r.data));
        } catch (e) {
            return e;
        }
    }
    @Action
    async delete(data: VisibilityRuleSet) {
        try {
            const r: any = await axios({
                method: 'DELETE',
                url: `/api/visibility_rule_sets/${data.id}`,
            });

            if (r.status !== 204) {
                throw Error('VisibilityRuleSet DELETE failed.');
            }
            this.context.commit('removeRuleSet', data);
        } catch (e) {
            return e;
        }
    }

    @Action
    async createRule({ set, data }: { set: VisibilityRuleSet; data: VisibilityRule }) {
        try {
            const r: any = await axios({
                method: 'POST',
                url: getUrl(data),
                data,
            });
            if (r.status !== 201) {
                throw Error('VisibilityRule CREATE failed.');
            }
            r.data.formElement = r.data.formElement && r.data.formElement['@id'];
            r.data.formElementTag = r.data.formElementTag && r.data.formElementTag['@id'];

            this.context.commit('addRule', { set, rule: new VisibilityRule(r.data) });
        } catch (e) {
            return e;
        }
    }

    @Action
    async updateRule({ set, data }: { set: VisibilityRuleSet; data: VisibilityRule }) {
        try {
            const r: any = await axios({
                method: 'PUT',
                url: `${getUrl(data)}/${data.id}`,
                data,
            });

            if (r.status !== 200) {
                throw Error('VisibilityRule UPDATE failed.');
            }
            if (r.data.formElementTag) {
                r.data.formElementTag = r.data.formElementTag['@id'];
            }
            if (r.data.formElement) {
                r.data.formElement = r.data.formElement['@id'];
            }
            this.context.commit('setRule', { set, rule: new VisibilityRule(r.data) });
        } catch (e) {
            return e;
        }
    }

    @Action
    async deleteRule({ set, data }: { set: VisibilityRuleSet; data: VisibilityRule }) {
        try {
            const r: any = await axios({
                method: 'DELETE',
                url: `${getUrl(data)}/${data.id}`,
            });

            if (r.status !== 204) {
                throw Error('VisibilityRule DELETE failed.');
            }
            this.context.commit('removeRule', { set, rule: data });
        } catch (e) {
            return e;
        }
    }
}

export default VisibilityRuleSetsModule;
